import { Box, IconButton, Drawer, useMediaQuery, Tooltip } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import MapView from '../../../components/MapComponent/MapView';
import ListIcon from '@mui/icons-material/List';
import CloseIcon from '@mui/icons-material/Close';
import LocationList from './LocationList';
import { LocationProvider } from './LocationContext';
import VehicleCamerasLivePanel from './VehicleCamerasLivePanel';

const LocationIndex = () => {
    const { t } = useTranslation();
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const isMobile = useMediaQuery('(max-width:900px)');
    const [sidebarOpen, setSidebarOpen] = useState(true);

    const sharedLocationListProps = {
        selectedIds,
        setSelectedIds,
    };

    const handleSelectDesktop = (id: string) => {
        setSelectedId(id);
    };

    const handleSelectMobile = (id: string) => {
        setSelectedId(id);
        setSidebarOpen(false);
    };

    return (
        <LocationProvider>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    height: '100vh',
                    gap: { xs: 1, md: 2 },
                    p: { xs: 1, md: 2 }
                }}
            >

                {!isMobile && sidebarOpen && (
                    <Box
                        sx={{
                            width: { md: 440, lg: 500 },
                            overflowY: 'auto',
                            position: 'relative',
                        }}
                    >
                        <LocationList
                            onSelect={handleSelectDesktop}
                            {...sharedLocationListProps}
                        />
                    </Box>
                )}

                {isMobile && (
                    <Drawer
                        anchor="left"
                        open={sidebarOpen}
                        onClose={() => setSidebarOpen(false)}
                        PaperProps={{
                            sx: { width: '100vw', maxWidth: 460 }
                        }}
                    >
                        <Box sx={{ width: '100%', p: 2 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 2
                                }}
                            >
                                <IconButton onClick={() => setSidebarOpen(false)}>
                                    <CloseIcon fontSize="medium" />
                                </IconButton>
                            </Box>

                            <LocationList
                                onSelect={handleSelectMobile}
                                {...sharedLocationListProps}
                            />
                        </Box>
                    </Drawer>
                )}

                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                    }}
                >
                    <Box
                        sx={{
                            flex: '0 0 auto',
                            position: 'relative',
                            borderRadius: { xs: 0, md: 3 },
                            overflow: 'hidden',
                            boxShadow: { md: 3 },
                            height: { xs: '82vh', md: '92vh' },
                            minHeight: { xs: 360, md: 500 },
                            maxHeight: { xs: 780, md: 980 },
                        }}
                    >
                        <Tooltip title={sidebarOpen ? t("map.closeList") : t("map.openList")}>
                            <IconButton
                                onClick={() => setSidebarOpen(prev => !prev)}
                                sx={{
                                    position: 'absolute',
                                    top: 80,
                                    left: 5,
                                    zIndex: 1000,
                                    backgroundColor: 'white',
                                    boxShadow: 2,
                                    borderRadius: '50%',
                                    width: 48,
                                    height: 48,
                                    "&:hover": {
                                        backgroundColor: "white",
                                        opacity: 1,
                                    },
                                }}
                            >
                                {sidebarOpen ? <CloseIcon /> : <ListIcon />}
                            </IconButton>
                        </Tooltip>

                        <MapView
                            selectedId={selectedId}
                            sidebarOpen={sidebarOpen}
                            selectedIds={selectedIds}
                        />

                        <Box
                            sx={{
                                position: 'absolute',
                                left: { xs: 8, md: 12 },
                                right: { xs: 8, md: 12 },
                                bottom: { xs: 8, md: 12 },
                                zIndex: 900,
                                maxHeight: { xs: '44%', md: '41%' },
                                overflowY: 'auto',
                                pointerEvents: 'auto',
                            }}
                        >
                            <VehicleCamerasLivePanel selectedId={selectedId} embedded />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </LocationProvider>
    );
};

export default LocationIndex;
